import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { BloomFilter } from '@/pixi/filters/BloomFilter'
import { NeonFilter } from '@/pixi/filters/NeonFilter'
import { EffectState } from '@/pixi/filters/types'
import * as PIXI from 'pixi.js'

interface NeonBlendUniforms {
  neonOutlineTexture: PIXI.Texture
  neonOutlineOffset: number
  useOffset: boolean
}
export class NeonBlendFilter extends BaseSeratoFilter {
  get uniforms (): NeonBlendUniforms {
    return super.uniforms as NeonBlendUniforms
  }

  readonly neon: NeonFilter
  readonly bloom: BloomFilter

  constructor (effectState: EffectState, useOffset = true) {
    const shader = `
    #define horizontalOffsetCoef 0.07
    #define verticalOffsetCoef 0.0

    // Built in parameters
    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;

    uniform sampler2D neonOutlineTexture;

    uniform float neonOutlineOffset;
    uniform bool useOffset;

    vec3 screen (vec3 base, vec3 blend) {
      return 1.0 - (1.0 - base) * (1.0 - blend);
    }

    vec2 zoom (vec2 textureCoord) {
      vec2 scaleCenter = vec2(0.5);
      float zoom = 0.88 + (1.0 - neonOutlineOffset) * 0.12;
      return (textureCoord - scaleCenter) * zoom + scaleCenter;
    }

    void main(void)
    {
      vec2 neonOutlineCoord = vTextureCoord;
      if (useOffset) {
        vec2 coordOffset = vec2(horizontalOffsetCoef * neonOutlineOffset, verticalOffsetCoef * neonOutlineOffset);
        neonOutlineCoord += coordOffset;
        
        // zoom in to hide the artifacts caused by the offset
        neonOutlineCoord = zoom(neonOutlineCoord);
      }
      
      vec2 originalTextureCoord = useOffset ? zoom(vTextureCoord) : vTextureCoord;
      vec4 originalColor = texture2D(uSampler, originalTextureCoord);

      vec4 neonOutline = texture2D(neonOutlineTexture, neonOutlineCoord);
      vec3 blend = screen(originalColor.rgb, neonOutline.rgb);
      gl_FragColor = vec4(blend, originalColor.a);
    }`

    super(
      effectState,
      undefined,
      shader
    )

    this.neon = new NeonFilter(effectState)
    this.bloom = new BloomFilter(effectState, {
      bloomScale: 1.6
    })
    this.uniforms.useOffset = useOffset
  }

  public updateUniforms () {
    this.uniforms.neonOutlineOffset = this.effectState.audioReactivityEnabled
      ? this.effectState.intensity * this.getAudioIntensityValue() : this.effectState.intensity
    this.neon.updateUniforms()
    this.bloom.updateUniforms()
  }

  /**
   * Overrides super class implementation. Used to get the frame of the applied neon effect
   * to pass in as a uniform
   */
  apply (
    filterManager: PIXI.FilterSystem,
    input: PIXI.RenderTexture,
    output: PIXI.RenderTexture,
    clearMode?: PIXI.CLEAR_MODES
  ): void {
    const tempTexture = filterManager.getFilterTexture()
    const tempTexture2 = filterManager.getFilterTexture()

    // Apply neon to the texture and save as temporary texture
    this.neon.apply(filterManager, input, tempTexture, PIXI.CLEAR_MODES.CLEAR)
    this.bloom.apply(filterManager, tempTexture, tempTexture2, PIXI.CLEAR_MODES.BLEND)
    this.uniforms.neonOutlineTexture = tempTexture2

    // Apply normal filter
    super.apply(filterManager, input, output, clearMode)
    filterManager.returnFilterTexture(tempTexture)
    filterManager.returnFilterTexture(tempTexture2)
  }
}
